import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Mail, ArrowRight, BookHeart, Sparkles, Check } from "lucide-react";
import { PrayerJournalPreview } from "./PrayerJournalPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREEBIE_URL = `${API}/freebies/prayer-journal.pdf`;

export const Newsletter = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // { limit, claimed, remaining }
    const [result, setResult] = useState(null); // server response after signup

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/freebies/founding-status`)
            .then((r) => mounted && setStatus(r.data))
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
            toast.error("Please enter a valid email.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/newsletter/subscribe`, { email });
            setResult(data);
            // Refresh the counter
            try {
                const s = await axios.get(`${API}/freebies/founding-status`);
                setStatus(s.data);
            } catch (_) {}
            if (data.is_founding && data.is_new) {
                toast.success(`Seat #${data.position} of ${data.position + data.founding_remaining} claimed 💜`);
            } else if (!data.is_new) {
                toast.success("You're already on the list. Check your inbox. 💜");
            } else {
                toast.success("You're in. Welcome to the becoming.");
            }
            setEmail("");
        } catch (err) {
            toast.error("Something went wrong. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const limit = status?.limit ?? 100;
    const remaining = status?.remaining ?? 100;
    const claimed = status?.claimed ?? 0;
    const founding_open = remaining > 0;
    const progress_pct = Math.min(100, Math.round((claimed / limit) * 100));

    // ----------------------- Post-signup success card ----------------------- //
    if (result && result.is_founding) {
        return (
            <section id="join" data-testid="newsletter-section" className="relative py-24 md:py-32">
                <div className="mx-auto max-w-4xl px-6 md:px-10">
                    <div
                        data-testid="founding-success-card"
                        className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-[#FAF5FC] via-white to-[#FFF7E5] border border-gold/30 p-10 md:p-16 shadow-[0_30px_80px_rgba(43,29,53,0.08)]"
                    >
                        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gold/20 blur-3xl" aria-hidden />
                        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-lilac-300/40 blur-3xl" aria-hidden />

                        <div className="relative text-center">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/40 text-gold-deep text-[11px] tracking-[0.18em] uppercase font-bold">
                                <Sparkles className="w-3.5 h-3.5" /> Founding 100
                            </div>
                            <h2 className="mt-6 font-display text-4xl md:text-5xl text-lilac-900 leading-tight">
                                You're seat{" "}
                                <em className="italic gold-text">#{result.position}</em> of {limit}.
                            </h2>
                            <p className="mt-5 text-lilac-700 leading-relaxed max-w-xl mx-auto">
                                Your welcome email is already on its way — with{" "}
                                <em>The Becoming Prayer Journal</em> tucked safely inside. You can
                                also download it right here.
                            </p>

                            <a
                                href={FREEBIE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="download-prayer-journal"
                                className="inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-full bg-gold hover:bg-gold-deep text-white font-semibold shadow-[0_14px_40px_rgba(212,175,55,0.45)] transition-all hover:-translate-y-0.5"
                            >
                                <BookHeart className="w-4 h-4" />
                                Download your Prayer Journal
                            </a>

                            <div className="mt-10 flex items-center justify-center gap-2 text-[11px] tracking-[0.18em] uppercase text-lilac-700">
                                <Check className="w-3.5 h-3.5 text-gold-deep" />
                                Check {result.email.replace(/(.{2}).*(@.*)/, "$1••••$2")} — inbox & spam
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (result && !result.is_founding && result.is_new) {
        return (
            <section id="join" data-testid="newsletter-section" className="relative py-24 md:py-32">
                <div className="mx-auto max-w-3xl px-6 md:px-10 text-center">
                    <div className="eyebrow text-gold-deep mb-5 inline-flex items-center gap-2">
                        <Check className="w-3.5 h-3.5" /> You're in
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl text-lilac-900 leading-tight">
                        Welcome to the <em className="italic gold-text">becoming</em>.
                    </h2>
                    <p className="mt-6 text-lilac-700 leading-relaxed max-w-lg mx-auto">
                        Every Sunday at sunrise, a soft note will find its way to you.
                    </p>
                </div>
            </section>
        );
    }

    // ----------------------- Default signup form ----------------------- //
    return (
        <section id="join" data-testid="newsletter-section" className="relative py-24 md:py-32">
            <div className="mx-auto max-w-6xl px-6 md:px-10">
                <div className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-lilac-200 via-lilac-100 to-[#FAF5FC] border border-lilac-200 p-10 md:p-16">
                    <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gold/20 blur-3xl" aria-hidden />
                    <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-lilac-300/40 blur-3xl" aria-hidden />

                    <div className="relative grid grid-cols-12 gap-8 items-center">
                        <div className="col-span-12 md:col-span-7">
                            <div className="eyebrow text-gold-deep mb-5 flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5" /> The Sunday Letter
                            </div>
                            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-lilac-900 leading-tight">
                                Soft notes for the <em className="italic gold-text">becoming</em>.
                            </h2>
                            <p className="mt-6 text-lilac-700 leading-relaxed max-w-lg">
                                A weekly love letter — scripture, soul prompts, and behind-the-mic moments
                                from upcoming episodes. Sent every Sunday at sunrise.
                            </p>

                            {/* Founding-100 freebie promise */}
                            {founding_open ? (
                                <div
                                    data-testid="founding-promo"
                                    className="mt-7 relative rounded-2xl bg-white/70 backdrop-blur border border-gold/30 p-5 md:p-6 shadow-[0_10px_30px_rgba(212,175,55,0.08)]"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="shrink-0 grid place-items-center w-11 h-11 rounded-xl bg-gold/15 text-gold-deep">
                                            <BookHeart className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="eyebrow text-gold-deep flex items-center gap-2">
                                                <span>Founding 100 · Free gift</span>
                                                <span
                                                    data-testid="founding-eyebrow-pill"
                                                    className="inline-flex items-center gap-1 rounded-full bg-gold/20 border border-gold/40 px-2 py-0.5 text-[9px] tracking-[0.14em] font-semibold text-gold-deep whitespace-nowrap"
                                                >
                                                    <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                                                    {remaining} SEAT{remaining === 1 ? "" : "S"} LEFT
                                                </span>
                                            </div>
                                            <p className="mt-1 text-lilac-900 font-semibold">
                                                The Becoming Prayer Journal — free for the first {limit} women in.
                                            </p>
                                            <p className="mt-1 text-sm text-lilac-700">
                                                Six soft days of scripture, reflection, and soul-truth — sent instantly
                                                when you join.
                                            </p>

                                            {/* progress bar */}
                                            <div className="mt-3 flex items-center gap-3">
                                                <div className="flex-1 h-1.5 rounded-full bg-lilac-200/70 overflow-hidden">
                                                    <div
                                                        className="h-full bg-gold transition-all duration-700"
                                                        style={{ width: `${progress_pct}%` }}
                                                    />
                                                </div>
                                                <span
                                                    data-testid="founding-remaining"
                                                    className="text-[11px] tracking-[0.14em] uppercase font-semibold text-gold-deep whitespace-nowrap"
                                                >
                                                    {remaining} seat{remaining === 1 ? "" : "s"} left
                                                </span>
                                            </div>

                                            <div className="mt-3">
                                                <PrayerJournalPreview />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-7 text-sm text-lilac-700 italic">
                                    The founding-100 Prayer Journal has been claimed — thank you. You're still welcome
                                    to join the Sunday Letter below. 💜
                                </div>
                            )}
                        </div>

                        <div className="col-span-12 md:col-span-5">
                            <form
                                onSubmit={submit}
                                data-testid="newsletter-form"
                                className="glass rounded-full p-1.5 flex items-center gap-1 shadow-[0_18px_50px_rgba(43,29,53,0.08)]"
                            >
                                <Input
                                    type="email"
                                    required
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="newsletter-email-input"
                                    className="border-0 bg-transparent rounded-full h-11 px-5 focus-visible:ring-0 shadow-none text-lilac-900 placeholder:text-lilac-700/60"
                                />
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="newsletter-submit"
                                    className="rounded-full h-11 px-6 bg-gold hover:bg-gold-deep text-white"
                                >
                                    {loading ? "Sending…" : founding_open ? "Claim seat" : "Join"}{" "}
                                    <ArrowRight className="w-4 h-4" />
                                </Button>
                            </form>
                            <p className="mt-4 eyebrow text-lilac-700/80">
                                No spam. Just the soft work.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
